import {
  Children,
  type ComponentPropsWithoutRef,
  type ReactElement,
  type ReactNode,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
} from 'react'

import { Button, type ButtonProps } from '@ps/ds/components/primitives/Button/Button'
import { cn } from '@ps/ds/lib/utils'
import { CardFooter as ShadCardFooter } from '@ps/ds/ui/card'

export type CardFooterPadding = 'none' | 'sm' | 'md' | 'lg'
export type CardFooterVariant = 'regular' | 'sticky' | 'fullWidth' | 'none'

type CardFooterSlotContextValue = {
  isFullWidth: boolean
}

const cardFooterSlotContext = createContext<CardFooterSlotContextValue>({
  isFullWidth: false,
})

const useCardFooterSlotContext = () => useContext(cardFooterSlotContext)

const cardFooterPaddingClasses: Record<CardFooterPadding, string> = {
  none: 'p-0',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
} as const

const cardFooterVariantClasses: Record<CardFooterVariant, string> = {
  regular: '',
  sticky: 'sticky bottom-0 z-10 bg-card border-t border-border',
  fullWidth: '',
  none: 'min-h-6',
} as const

const baseSideGroupClass = 'flex items-center gap-2'

export interface CardFooterLeftProps extends ComponentPropsWithoutRef<'div'> {}

export interface CardFooterRightProps extends ComponentPropsWithoutRef<'div'> {}

export interface CardFooterProps extends ComponentPropsWithoutRef<typeof ShadCardFooter> {
  // Footer layout style.
  variant?: CardFooterVariant

  // Footer spacing scale.
  padding?: CardFooterPadding
}

type ResolvedSlotChildrenOptions = {
  isFullWidth: boolean
  defaultVariant?: ButtonProps['variant']
}

const isButtonElement = (child: ReactNode): child is ReactElement<ButtonProps, typeof Button> =>
  isValidElement(child) && child.type === Button

// Normalizes footer slot children by applying layout-driven button props.
// - Reverses child order in fullWidth mode to match design ordering.
// - Expands buttons to full width when stacked.
// - Applies a default variant when a button omits one (used by left slot).
const getResolvedSlotChildren = (
  children: ReactNode,
  { isFullWidth, defaultVariant }: ResolvedSlotChildrenOptions,
) => {
  const childArray = Children.toArray(children)
  const orderedChildren = isFullWidth ? [...childArray].reverse() : childArray

  return orderedChildren.map((child, index) => {
    if (!isButtonElement(child)) {
      return child
    }

    const buttonChild = child

    return cloneElement(buttonChild, {
      key: buttonChild.key ?? `card-footer-button-${index}`,
      className: cn(isFullWidth && 'w-full', buttonChild.props.className),
      variant: buttonChild.props.variant ?? defaultVariant,
    })
  })
}

export const CardFooterLeft = ({ className, children, ...props }: CardFooterLeftProps) => {
  const { isFullWidth } = useCardFooterSlotContext()

  return (
    <div className={cn(baseSideGroupClass, isFullWidth && 'w-full flex-col', className)} {...props}>
      {getResolvedSlotChildren(children, {
        isFullWidth,
        defaultVariant: 'secondary',
      })}
    </div>
  )
}

export const CardFooterRight = ({ className, children, ...props }: CardFooterRightProps) => {
  const { isFullWidth } = useCardFooterSlotContext()

  return (
    <div
      className={cn(
        baseSideGroupClass,
        isFullWidth && 'w-full flex-col',
        !isFullWidth && 'ml-auto',
        className,
      )}
      {...props}>
      {getResolvedSlotChildren(children, { isFullWidth })}
    </div>
  )
}

const isFooterLeft = (child: ReactNode) => isValidElement(child) && child.type === CardFooterLeft

const isFooterRight = (child: ReactNode) => isValidElement(child) && child.type === CardFooterRight

export const CardFooter = ({
  className,
  variant = 'regular',
  padding = 'lg',
  children,
  ...props
}: CardFooterProps) => {
  const isNone = variant === 'none'
  const isFullWidth = variant === 'fullWidth'
  const childArray = Children.toArray(children)
  const leftGroup = childArray.find(isFooterLeft) ?? null
  const rightGroup = childArray.find(isFooterRight) ?? null

  return (
    <ShadCardFooter
      className={cn(
        'w-full',
        cardFooterPaddingClasses[padding],
        cardFooterVariantClasses[variant],
        className,
      )}
      {...props}>
      {!isNone && (
        <cardFooterSlotContext.Provider value={{ isFullWidth }}>
          <div className={cn('flex w-full gap-2', isFullWidth ? 'flex-col' : 'items-center')}>
            {isFullWidth ? rightGroup : leftGroup}
            {isFullWidth ? leftGroup : rightGroup}
          </div>
        </cardFooterSlotContext.Provider>
      )}
    </ShadCardFooter>
  )
}
